import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Loader2, Send, Eye, FlaskConical, Users } from "lucide-react";

export function EmailPreviewFrame({ html, testid }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-[#F5F7FA]" data-testid={testid}>
      <iframe title="Email preview" srcDoc={html} sandbox="" className="h-[560px] w-full" />
    </div>
  );
}

export default function ComposeAnnouncement({ onSent }) {
  const [form, setForm] = useState({ subject: "", message: "", audience: "all", cta_label: "", cta_url: "" });
  const [audiences, setAudiences] = useState([]);
  const [optedOut, setOptedOut] = useState(0);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e })); setPreview(null); };

  useEffect(() => {
    api.get("/admin/email/audiences").then((r) => { setAudiences(r.data.audiences || []); setOptedOut(r.data.opted_out || 0); }).catch(() => {});
  }, []);

  const payload = { ...form, cta_label: form.cta_label || null, cta_url: form.cta_url || null };
  const current = audiences.find((a) => a.key === form.audience);
  const ready = form.subject.trim() && form.message.trim();

  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "preview") { setPreview((await api.post("/admin/email/announcements/preview", payload)).data); }
      else if (kind === "test") { toast.success((await api.post("/admin/email/announcements/test", payload)).data.message); }
      else {
        const r = (await api.post("/admin/email/announcements", payload)).data;
        toast.success(`Sending to ${r.recipients_count} member${r.recipients_count === 1 ? "" : "s"} — about a second apart`);
        setForm({ subject: "", message: "", audience: "all", cta_label: "", cta_url: "" }); setPreview(null);
        onSent?.(r);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(null); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-testid="compose-announcement">
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="ann-audience">To</Label>
          <Select value={form.audience} onValueChange={set("audience")}>
            <SelectTrigger id="ann-audience" data-testid="announcement-audience-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {audiences.map((a) => <SelectItem key={a.key} value={a.key} data-testid={`announcement-audience-${a.key.replace(":", "-")}`}>{a.label} · {a.count}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground" data-testid="announcement-audience-note">
            <Users className="mr-1 inline h-3.5 w-3.5" />{current ? `${current.count} will receive this.` : ""}{optedOut > 0 ? ` ${optedOut} member${optedOut === 1 ? " has" : "s have"} opted out of non-essential email and are skipped automatically.` : ""}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ann-subject">Subject</Label>
          <Input id="ann-subject" value={form.subject} onChange={set("subject")} placeholder="This Friday we're at Hotel Indigo — and a small favor" maxLength={150} data-testid="announcement-subject-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ann-message">Message</Label>
          <Textarea id="ann-message" value={form.message} onChange={set("message")} rows={9} placeholder={"Hi {first_name},\n\nWrite like you talk. Blank line = new paragraph."} data-testid="announcement-message-input" />
          <p className="text-xs text-muted-foreground">Use <code className="rounded bg-secondary px-1">{"{first_name}"}</code> to personalise. Every email ends with an “Email preferences” link so people can opt out.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5"><Label htmlFor="ann-cta">Button label (optional)</Label><Input id="ann-cta" value={form.cta_label} onChange={set("cta_label")} placeholder="RSVP for Friday" maxLength={40} data-testid="announcement-cta-label-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="ann-cta-url">Button link</Label><Input id="ann-cta-url" value={form.cta_url} onChange={set("cta_url")} placeholder="https://fridayconversations.live/events/…" data-testid="announcement-cta-url-input" /></div>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button variant="outline" disabled={!ready || !!busy} onClick={() => run("preview")} data-testid="announcement-preview-button">{busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Eye className="mr-2 h-4 w-4" />Preview</>}</Button>
          <Button variant="outline" disabled={!ready || !!busy} onClick={() => run("test")} data-testid="announcement-test-button">{busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FlaskConical className="mr-2 h-4 w-4" />Send me a test</>}</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={!ready || !!busy || !current?.count} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="announcement-send-button"><Send className="mr-2 h-4 w-4" />Send to {current?.count ?? 0}</Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="announcement-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Send “{form.subject}”?</AlertDialogTitle>
                <AlertDialogDescription>This goes to {current?.count} member{current?.count === 1 ? "" : "s"} ({current?.label}) right now, about a second apart. There’s no unsend.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="announcement-confirm-cancel">Not yet</AlertDialogCancel>
                <AlertDialogAction onClick={() => run("send")} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="announcement-confirm-send">Send it</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      <div>
        {preview ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground" data-testid="announcement-preview-subject"><span className="font-medium text-foreground">Subject:</span> {preview.subject}</p>
            <EmailPreviewFrame html={preview.html} testid="announcement-preview-frame" />
          </div>
        ) : (
          <div className="flex h-full min-h-[280px] items-center justify-center rounded-xl border border-dashed border-border bg-secondary/30 p-6 text-center text-sm text-muted-foreground" data-testid="announcement-preview-empty">
            Write your message, then hit Preview to see exactly what members get.
          </div>
        )}
      </div>
    </div>
  );
}
